import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import SignatureCanvas from 'react-signature-canvas';
import { FiUpload, FiX, FiCamera, FiFile, FiTrash2, FiCheck } from 'react-icons/fi';
import heic2any from 'heic2any';
import Modal from '../../../components/Modal';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Button from '../../../components/Button';
import Api from '../../../Api';
import '../../../components/CompanyForm/style.css';
import './style.css';

// Utility functions
function base64ToBlob(base64, type) {
  try {
    const parts = base64.split(';base64,');
    const contentType = parts[0].split(':')[1] || type;
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: contentType });
  } catch (e) {
    console.error('Error converting base64 to blob', e);
    return null;
  }
}

function PreviewImage({ photo, className }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isActive = true;
    // Priority: preview (new upload) > data (existing) > content (existing alternate)
    const rawData = photo.preview || photo.data || photo.content;
    const type = photo.type || '';
    
    // Check if it's HEIC
    if (type.includes('heic') || (typeof rawData === 'string' && rawData.startsWith('data:image/heic'))) {
      setLoading(true);
      // Need blob for conversion
      const blob = base64ToBlob(rawData, 'image/heic');
      if (blob) {
        heic2any({
          blob,
          toType: "image/jpeg",
          quality: 0.8
        })
        .then((conversionResult) => {
          if (isActive) {
            const resultBlob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
            const url = URL.createObjectURL(resultBlob);
            setSrc(url);
            setLoading(false);
          }
        })
        .catch((e) => {
          console.error("HEIC conversion failed", e);
          if (isActive) {
            setSrc(rawData); // Fallback
            setLoading(false);
          }
        });
      } else {
        if (isActive) {
          setSrc(rawData);
          setLoading(false);
        }
      }
    } else {
      if (isActive) {
        setSrc(rawData);
        setLoading(false);
      }
    }

    return () => {
      isActive = false;
      // Cleanup object URL if created
    };
  }, [photo]);

  if (loading) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: '12px' }}>
        Convertendo...
      </div>
    );
  }

  return <img src={src} alt="preview" className={className} />;
}

function formatCurrency(value) {
  if (!value) return '';
  let v = value.replace(/\D/g, '');
  v = (v / 100).toFixed(2) + '';
  v = v.replace('.', ',');
  v = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  return `R$ ${v}`;
}

function formatPhone(value) {
  if (!value) return '';
  let v = value.replace(/\D/g, '');
  if (v.length > 11) v = v.slice(0, 11);

  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0,2)}) ${v.slice(2)}`;
  if (v.length <= 10) return `(${v.slice(0,2)}) ${v.slice(2,6)}-${v.slice(6)}`;
  return `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
}

export default function ExecutionModal({ isOpen, onClose, inspection, executionData, onSuccess, companyId }) {
  const [fullInspection, setFullInspection] = useState(inspection);
  const [loadingInspection, setLoadingInspection] = useState(false);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [stations, setStations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [machineries, setMachineries] = useState([]);
  const signaturePadRefs = useRef({});
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const [company] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedCompany'));
    } catch (e) {
      return null;
    }
  });
  
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('authUser'));
    } catch (e) {
      return null;
    }
  });


  const questionMap = React.useMemo(() => {
    if (!fullInspection?.sections) return {};
    const map = {};
    fullInspection.sections.forEach(section => {
      section.questions.forEach(q => {
        map[q.id] = q;
      });
    });
    return map;
  }, [fullInspection]);

  useEffect(() => {
    if (isOpen && company?.id) {
      if (executionData) {
        // Editing Mode
        setAnswers(executionData.answers || {});
        
        // If execution has config loaded, use it. Otherwise fetch.
        if (executionData.config) {
          if (!executionData.config.sections || executionData.config.sections.length === 0) {
            setLoadingInspection(true);
            Api.getInspectionConfigById(companyId || company.id, executionData.inspection_config_id)
              .then(data => {
                setFullInspection(data);
              })
              .catch(error => {
                console.error('Error fetching inspection details:', error);
                toast.error('Erro ao carregar detalhes da inspeção.');
              })
              .finally(() => {
                setLoadingInspection(false);
              });
          } else {
            setFullInspection(executionData.config);
          }
        } else if (executionData.inspection_config_id) {
           setLoadingInspection(true);
           Api.getInspectionConfigById(companyId || company.id, executionData.inspection_config_id)
            .then(data => {
              setFullInspection(data);
            })
            .catch(error => {
               console.error('Error fetching inspection details:', error);
               toast.error('Erro ao carregar detalhes da inspeção.');
            })
            .finally(() => {
              setLoadingInspection(false);
            });
        }

      } else if (inspection) {
        // New Execution Mode
        setAnswers({});
        // Check if we have sections, if not, fetch full details
        if (!inspection.sections || inspection.sections.length === 0) {
          setLoadingInspection(true);
          Api.getInspectionConfigById(companyId || company.id, inspection.id)
            .then(data => {
              setFullInspection(data);
            })
            .catch(error => {
              console.error('Error fetching inspection details:', error);
              toast.error('Erro ao carregar detalhes da inspeção.');
            })
            .finally(() => {
              setLoadingInspection(false);
            });
        } else {
          setFullInspection(inspection);
        }
      }
    }
  }, [isOpen, inspection, executionData, company?.id, companyId]);

  useEffect(() => {
    if (isOpen && !executionData) {
      // Only reset answers if NOT editing
      setAnswers({});
      // Reset signature pads
      Object.values(signaturePadRefs.current).forEach(ref => ref?.clear());
    }
    
    // Load signatures if editing
    if (isOpen && executionData && answers) {
      setTimeout(() => {
        Object.keys(answers).forEach(key => {
           const value = answers[key];
           // Check if it looks like a signature (base64 png usually) and if we have a ref
           // We can also check question type if we had easy access here, but checking ref is enough
           if (typeof value === 'string' && value.startsWith('data:image/') && signaturePadRefs.current[key]) {
             // Only load if the canvas is empty or we want to force it. 
             // Ideally check if empty, but for edit load we overwrite.
             if (signaturePadRefs.current[key].isEmpty()) {
                signaturePadRefs.current[key].fromDataURL(value);
             }
           }
        });
      }, 500); // Small delay to ensure canvas is ready
    }
  }, [isOpen, fullInspection, executionData]);

  useEffect(() => {
    async function fetchData() {
      // Fetch data from ALL accessible companies using the optimized 'all' endpoint
      try {
        let companies = [];
        try {
           const companiesResponse = await Api.getCompanies();
           companies = Array.isArray(companiesResponse) ? companiesResponse : (companiesResponse.data || []);
        } catch (err) {
           console.error("Error fetching companies:", err);
        }

        const companyMap = {};
        companies.forEach(c => {
          companyMap[c.id] = c.trade_name || c.corporate_name;
        });

        // Use 'all' to fetch combined data from all companies the user has access to
        const results = await Promise.allSettled([
          Api.getEmployees('all'),
          Api.getServiceStations('all'),
          Api.getVehicles('all'),
          Api.getMachineries('all')
        ]);

        const empRes = results[0].status === 'fulfilled' ? results[0].value : [];
        const stationRes = results[1].status === 'fulfilled' ? results[1].value : [];
        const vehicleRes = results[2].status === 'fulfilled' ? results[2].value : [];
        const machineryRes = results[3].status === 'fulfilled' ? results[3].value : [];

        if (results[0].status === 'rejected') console.error('Error fetching employees:', results[0].reason);
        if (results[1].status === 'rejected') console.error('Error fetching stations:', results[1].reason);
        if (results[2].status === 'rejected') console.error('Error fetching vehicles:', results[2].reason);
        if (results[3].status === 'rejected') console.error('Error fetching machineries:', results[3].reason);

        const allEmployees = Array.isArray(empRes) ? empRes : (empRes.data || []);
        const allStations = Array.isArray(stationRes) ? stationRes : (stationRes.data || []);
        const allVehicles = Array.isArray(vehicleRes) ? vehicleRes : (vehicleRes.data || []);
        const allMachineries = Array.isArray(machineryRes) ? machineryRes : (machineryRes.data || []);

        // Attach company names for display
        allEmployees.forEach(e => {
            if (e.company && (e.company.trade_name || e.company.corporate_name)) {
                e.companyName = e.company.trade_name || e.company.corporate_name;
            } else if (e.company_id && companyMap[e.company_id]) {
                e.companyName = companyMap[e.company_id];
            }
        });

        allStations.forEach(s => {
            if (s.company && (s.company.trade_name || s.company.corporate_name)) {
                s.companyName = s.company.trade_name || s.company.corporate_name;
            } else if (s.company_id && companyMap[s.company_id]) {
                s.companyName = companyMap[s.company_id];
            }
        });
        
        allVehicles.forEach(v => {
            // Priority to company object if returned by backend, else use map
            if (v.company && (v.company.trade_name || v.company.corporate_name)) {
                v.companyName = v.company.trade_name || v.company.corporate_name;
            } else if (v.company_id && companyMap[v.company_id]) {
                v.companyName = companyMap[v.company_id];
            }
        });

        allMachineries.forEach(m => {
            if (m.company && (m.company.trade_name || m.company.corporate_name)) {
                m.companyName = m.company.trade_name || m.company.corporate_name;
            } else if (m.company_id && companyMap[m.company_id]) {
                m.companyName = companyMap[m.company_id];
            }
        });

        setEmployees(allEmployees);
        setStations(allStations);
        setVehicles(allVehicles);
        setMachineries(allMachineries);

      } catch (error) {
        console.error('Error loading data:', error);
        // Don't show error toast for data loading to avoid spamming if one fails, just log it
      }
    }

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]); // Removed companyId dependencies as we fetch 'all' regardless

  useEffect(() => {
    if (isOpen && fullInspection?.geolocation_required && !executionData) {
      // Only require new location for NEW executions
      setLocation(null);
      setLocationError(null);

      if (!navigator.geolocation) {
        setLocationError('Geolocalização não suportada pelo navegador.');
        toast.error('Geolocalização não suportada pelo navegador.');
        return;
      }
      
      toast.info('Obtendo sua localização...', { autoClose: 2000 });

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationError(null);
          toast.success('Localização obtida com sucesso!');
        },
        (error) => {
          console.error("Error getting location:", error);
          let msg = 'Erro ao obter localização.';
          if (error.code === 1) msg = 'Permissão de localização negada. Ative a localização no navegador.';
          if (error.code === 2) msg = 'Localização indisponível.';
          if (error.code === 3) msg = 'Tempo limite para obter localização esgotado.';
          setLocationError(msg);
          toast.error(msg);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  }, [isOpen, fullInspection, executionData]);

  if (!isOpen) return null; // Prevent rendering if not open
  if (!fullInspection && !loadingInspection) return null;

  function handleAnswerChange(questionId, value) {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));

    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  }

  function handleFileChange(questionId, files) {
    // Convert FileList to Array
    const newFiles = Array.from(files);
    
    // Create preview URLs
    const filesWithPreview = newFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      type: file.type
    }));

    setAnswers(prev => {
      const currentFiles = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: [...currentFiles, ...filesWithPreview]
      };
    });

    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  }

  function removeFile(questionId, index) {
    setAnswers(prev => {
      const currentFiles = [...(prev[questionId] || [])];
      currentFiles.splice(index, 1);
      return {
        ...prev,
        [questionId]: currentFiles
      };
    });
  }

  function handleSignatureEnd(questionId) {
    const ref = signaturePadRefs.current[questionId];
    if (ref) {
      const signatureData = ref.toDataURL();
      handleAnswerChange(questionId, signatureData);
    }
  }

  function clearSignature(questionId) {
    const ref = signaturePadRefs.current[questionId];
    if (ref) {
      ref.clear();
      handleAnswerChange(questionId, null);
    }
  }

  function handleMonetaryChange(questionId, value) {
    // Keep only numbers for raw value if needed, but here we store formatted string or raw number?
    // Let's store the formatted string for display, or better:
    // Store raw number, format on render. But Input needs value.
    // Let's implement a simple mask behavior.
    
    let v = value.replace(/\D/g, '');
    // Limit length if needed
    handleAnswerChange(questionId, v);
  }

  function handlePhoneChange(questionId, value) {
    let v = value.replace(/\D/g, '');
    if (v.length > 11) v = v.slice(0, 11);
    handleAnswerChange(questionId, v);
  }

  function isQuestionVisible(question, visited = new Set()) {
    if (!question.conditional?.enabled) return true;
    
    const { referenceId, expectedValue } = question.conditional;
    if (!referenceId) return true;

    // Cycle detection
    if (visited.has(question.id)) return true;
    visited.add(question.id);

    // Find the reference question
    const refQuestion = questionMap[referenceId];
    
    // If reference question exists, check if it is visible first
    if (refQuestion) {
      if (!isQuestionVisible(refQuestion, new Set(visited))) return false;
    }

    const refAnswer = answers[referenceId];
    
    if (Array.isArray(refAnswer)) {
      return refAnswer.includes(expectedValue);
    }
    
    // Strict comparison for non-array values to avoid "undefined" === "undefined" false positives if expectedValue is empty
    if (refAnswer === undefined || refAnswer === null) {
       if (expectedValue === 'nao_contem') return true;
       return false;
    }
    
    // Handle "Contém" / "Não Contém" special logic
    if (expectedValue === 'contem') {
      if (Array.isArray(refAnswer)) return refAnswer.length > 0;
      if (typeof refAnswer === 'string') return refAnswer.trim().length > 0;
      return true; // value exists and is not null/undefined
    }

    if (expectedValue === 'nao_contem') {
      if (Array.isArray(refAnswer)) return refAnswer.length === 0;
      if (typeof refAnswer === 'string') return refAnswer.trim().length === 0;
      return false; // value exists, so "not contains" is false (assuming "not contains" means empty)
    }

    return String(refAnswer) === String(expectedValue);
  }

  function renderInput(question) {
    const rawValue = answers[question.id];
    const value = rawValue || '';

    switch (question.type) {
      case 'text':
      case 'email':
        return (
          <Input
            value={value}
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            placeholder="Sua resposta..."
            noMargin
            required={question.required}
          />
        );

      case 'long_text':
        return (
          <Input
            multiline
            rows={4}
            value={value}
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            placeholder="Digite sua resposta detalhada..."
            required={question.required}
            noMargin
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            placeholder="0"
            noMargin
            required={question.required}
          />
        );

      case 'monetary':
        return (
          <Input
            value={formatCurrency(value)}
            onChange={e => handleMonetaryChange(question.id, e.target.value)}
            placeholder="R$ 0,00"
            noMargin
            required={question.required}
          />
        );

      case 'phone':
        return (
          <Input
            value={formatPhone(value)}
            onChange={e => handlePhoneChange(question.id, e.target.value)}
            placeholder="(XX) XXXXX-XXXX"
            noMargin
            required={question.required}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            noMargin
            required={question.required}
          />
        );
        
      case 'time':
        return (
          <Input
            type="time"
            value={value}
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            noMargin
            required={question.required}
          />
        );

      case 'single_choice':
        return (
          <div className="radio-group">
            {question.options?.map((option, idx) => (
              <div 
                key={idx} 
                className={`custom-radio ${
                  value === option.text ||
                  String(value) === String(option.id) ||
                  (option.value && String(value) === String(option.value))
                    ? 'selected'
                    : ''
                }`}
                onClick={() => handleAnswerChange(question.id, option.text)}
              >
                <div className="radio-indicator" />
                <span>{option.text}</span>
              </div>
            ))}
          </div>
        );

      case 'multiple_choice':
        const rawValues = Array.isArray(rawValue)
          ? rawValue
          : rawValue
          ? [rawValue]
          : [];
        const rawSet = new Set(rawValues.map(v => String(v)));
        const canonicalSelectedTexts =
          question.options
            ?.filter(opt =>
              rawSet.has(String(opt.text)) ||
              rawSet.has(String(opt.id)) ||
              (opt.value && rawSet.has(String(opt.value)))
            )
            .map(opt => opt.text) || [];
        return (
          <div className="checkbox-group">
            {question.options?.map((option, idx) => {
              const isSelected = canonicalSelectedTexts.includes(option.text);
              return (
                <div 
                  key={idx} 
                  className={`custom-checkbox ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    let newValues;
                    if (isSelected) {
                      newValues = canonicalSelectedTexts.filter(v => v !== option.text);
                    } else {
                      newValues = [...canonicalSelectedTexts, option.text];
                    }
                    handleAnswerChange(question.id, newValues);
                  }}
                >
                  <div className="checkbox-indicator">
                    {isSelected && <FiCheck size={14} />}
                  </div>
                  <span>{option.text}</span>
                </div>
              );
            })}
          </div>
        );

      case 'photo':
        const photos = Array.isArray(value) ? value : [];
        return (
          <div className="file-upload-container">
            <div className="upload-dropzone" onClick={() => document.getElementById(`file-${question.id}`).click()}>
              <FiCamera size={24} />
              <span>Clique para adicionar fotos</span>
              <input
                id={`file-${question.id}`}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => handleFileChange(question.id, e.target.files)}
              />
            </div>
            {photos.length > 0 && (
              <div className="preview-grid">
                {photos.map((photo, idx) => (
                  <div key={idx} className="preview-item">
                    <PreviewImage photo={photo} className="preview-image" />
                    <button 
                      type="button" 
                      className="remove-file-btn"
                      onClick={() => removeFile(question.id, idx)}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'document':
        const docs = Array.isArray(value) ? value : [];
        return (
          <div className="file-upload-container">
            <div className="upload-dropzone" onClick={() => document.getElementById(`doc-${question.id}`).click()}>
              <FiUpload size={24} />
              <span>Clique para adicionar documentos</span>
              <input
                id={`doc-${question.id}`}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => handleFileChange(question.id, e.target.files)}
              />
            </div>
            {docs.length > 0 && (
              <div className="preview-grid">
                {docs.map((doc, idx) => (
                  <div key={idx} className="preview-item">
                    <div className="preview-file">
                      <FiFile size={24} />
                      <span style={{ wordBreak: 'break-word' }}>{doc.name}</span>
                    </div>
                    <button 
                      type="button" 
                      className="remove-file-btn"
                      onClick={() => removeFile(question.id, idx)}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'signature':
        return (
          <div className="signature-container">
            <SignatureCanvas
              ref={(ref) => signaturePadRefs.current[question.id] = ref}
              penColor="black"
              canvasProps={{
                className: 'signature-pad'
              }}
              onEnd={() => handleSignatureEnd(question.id)}
            />
            <div className="signature-actions">
              <button 
                type="button" 
                className="clear-signature-btn"
                onClick={() => clearSignature(question.id)}
              >
                <FiTrash2 size={14} />
                Limpar Assinatura
              </button>
            </div>
          </div>
        );

      case 'employee':
        return (
           <Select
             options={employees.map(e => ({ 
               value: String(e.id), 
               label: e.companyName ? `${e.name} - ${e.companyName}` : e.name 
             }))}
             value={value ? String(value) : ''}
             onChange={e => handleAnswerChange(question.id, e.target.value)}
             placeholder="Selecione um funcionário..."
             noMargin
             searchable
             required={question.required}
           />
        );

      case 'station':
        return (
           <Select
             options={stations.map(s => ({ 
               value: String(s.id), 
               label: s.companyName ? `${s.name} - ${s.companyName}` : s.name 
             }))}
             value={value ? String(value) : ''}
             onChange={e => handleAnswerChange(question.id, e.target.value)}
             placeholder="Selecione um posto..."
             noMargin
             searchable
             required={question.required}
           />
        );

      case 'vehicle':
        return (
           <Select
             options={vehicles.map(v => {
               const id = v.id ?? v.code ?? v.vehicle_id ?? v.veiculo_id;
               const plate = v.plate || v.license_plate || v.placa || v.code || '';
               const name = v.name || v.description || v.descricao || v.modelo || v.model || '';
               const baseLabel = name || `Veículo ${id}`;
               let label = plate ? `${plate} - ${baseLabel}` : baseLabel;
               if (v.companyName) {
                 label += ` - ${v.companyName}`;
               }
               return { value: String(id), label };
             })}
             value={value ? String(value) : ''}
             onChange={e => handleAnswerChange(question.id, e.target.value)}
             placeholder="Selecione um veículo..."
             noMargin
             searchable
             required={question.required}
           />
        );

      case 'machinery':
      case 'machinery_select':
        return (
           <Select
             options={machineries.map(m => {
               const id = m.id;
               const tag = m.tag || '';
               const model = m.model || '';
               const brand = m.brand || '';
               let label = `${tag} - ${model}`;
               if (brand) label += ` (${brand})`;
               if (m.companyName) label += ` - ${m.companyName}`;
               return { value: String(id), label };
             })}
             value={value ? String(value) : ''}
             onChange={e => handleAnswerChange(question.id, e.target.value)}
             placeholder="Selecione um maquinário..."
             noMargin
             searchable
             required={question.required}
           />
        );

      case 'select':
        return (
           <Select
             options={question.options?.map(o => ({ 
               value: String(o.value || o.id || o.text), 
               label: o.text 
             })) || []}
             value={value ? String(value) : ''}
             onChange={e => handleAnswerChange(question.id, e.target.value)}
             placeholder="Selecione uma opção..."
             noMargin
             searchable
             required={question.required}
           />
        );

      default:
        return (
          <Input
            value={value}
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            placeholder="Resposta..."
            noMargin
          />
        );
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Validate Geolocation
    if (fullInspection.geolocation_required && !executionData) {
      if (locationError) {
        toast.error(`Geolocalização obrigatória: ${locationError}`);
        return;
      }
      if (!location) {
        toast.error('Aguardando localização obrigatória. Verifique se o GPS está ativo.');
        return;
      }
    }

    // Validation
    const newErrors = {};
    let hasError = false;

    for (const section of fullInspection.sections) {
      for (const question of section.questions) {
        if (isQuestionVisible(question) && question.required) {
          const answer = answers[question.id];
          if (
            answer === undefined || 
            answer === null || 
            answer === '' || 
            (Array.isArray(answer) && answer.length === 0)
          ) {
            newErrors[question.id] = true;
            hasError = true;
          }
        }
      }
    }

    if (hasError) {
      setErrors(newErrors);
      toast.error('Por favor, responda todas as perguntas obrigatórias.');
      // Attempt to scroll to first error
      const firstErrorId = Object.keys(newErrors)[0];
      if (firstErrorId) {
        const el = document.getElementById(`question-${firstErrorId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setErrors({});

    try {
      setSubmitting(true);
      
      // Filter out answers from invisible questions
      const visibleAnswers = {};
      for (const section of fullInspection.sections) {
        for (const question of section.questions) {
          if (isQuestionVisible(question)) {
             if (answers[question.id] !== undefined) {
               visibleAnswers[question.id] = answers[question.id];
             }
          }
        }
      }
      
      // Process answers to convert files/signatures/monetary if needed
      const processedAnswers = { ...visibleAnswers };
      
      // For now, we're just sending what we have. 
      // Files are objects with preview blobs. We should probably convert them to base64 
      // or upload them first. Given the scope, I'll strip the preview URL and just send metadata 
      // or convert to base64 if small enough.
      // Let's implement a simple base64 conversion for this task to be complete.
      
      for (const key in processedAnswers) {
        const value = processedAnswers[key];
        if (Array.isArray(value) && value.length > 0) {
          // It's a file array (potentially mixed new and existing)
          processedAnswers[key] = await Promise.all(value.map(async (item) => {
            if (item.file) {
              // New file, convert to base64
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve({
                  name: item.name,
                  type: item.type,
                  data: reader.result // Base64
                });
                reader.readAsDataURL(item.file);
              });
            } else {
              // Existing file, keep as is
              return item;
            }
          }));
        }
      }

      const payload = {
        inspection_config_id: fullInspection.id,
        inspection_name: fullInspection.name,
        executed_at: executionData ? executionData.executed_at : new Date().toISOString(),
        executor_id: user?.id,
        executor_name: user?.name,
        executor_type: 'user',
        latitude: location?.latitude,
        longitude: location?.longitude,
        answers: processedAnswers
      };

      if (executionData && executionData.id) {
         await Api.updateExecutedInspection(companyId || company.id, executionData.id, payload);
         toast.success('Inspeção atualizada com sucesso!');
      } else {
         await Api.createExecutedInspection(companyId || company.id, payload);
         toast.success('Inspeção realizada com sucesso!');
      }
      
      onSuccess();
      onClose();

    } catch (error) {
      console.error('Error saving inspection:', error);
      toast.error('Erro ao salvar inspeção.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={fullInspection ? `Executar: ${fullInspection.name}` : 'Executar Inspeção'}
      closeOnOverlayClick={false}
      size="lg"
    >
      {loadingInspection ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando formulário...</div>
      ) : (fullInspection?.geolocation_required && !executionData && !location) ? (
        <div style={{ padding: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          {locationError ? (
            <>
               <div style={{ color: 'var(--danger-color)', fontWeight: 'bold' }}>
                 ⚠️ Geolocalização Obrigatória
               </div>
               <p>{locationError}</p>
               <Button onClick={onClose} variant="secondary">Fechar</Button>
            </>
          ) : (
            <>
              <div className="spinner" style={{ width: '24px', height: '24px', border: '3px solid rgba(0,0,0,0.1)', borderTop: '3px solid var(--primary-color)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              <p>Obtendo localização obrigatória...</p>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Por favor, aguarde e permita o acesso à sua localização.</p>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="company-form-component">
          <div className="form-body">
            {fullInspection.sections.map(section => (
              <div key={section.id} className="execution-section">
                <div className="execution-section-header">
                  {section.title}
                </div>
                <div className="execution-section-body">
                  {section.questions.map(question => {
                    if (!isQuestionVisible(question)) return null;

                    const hasError = errors[question.id];

                    return (
                      <div 
                        key={question.id} 
                        id={`question-${question.id}`}
                        className="execution-question"
                        style={hasError ? { borderColor: 'var(--danger-color)', backgroundColor: '#fff5f5' } : {}}
                      >
                        <label className="execution-question-label" style={hasError ? { color: 'var(--danger-color)' } : {}}>
                          {question.text}
                          {question.required && <span className="required-mark">*</span>}
                        </label>
                        {renderInput(question)}
                        {hasError && (
                           <div style={{ color: 'var(--danger-color)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                             <FiX size={12} />
                             <span>Campo obrigatório</span>
                           </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              isLoading={submitting}
              variant="primary"
            >
              Salvar
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
